import { withErrorBoundary } from 'app/components/error-boundary';
import { InfoChat } from 'app/design/icons/info-chat';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Calendar } from 'app/design/primitives/calendar';
import { Checkbox } from 'app/design/primitives/checkbox';
import { Page } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Text } from 'app/design/primitives/text';

import { useForm } from 'app/hooks/form';
import { useNavigationOptions } from 'app/hooks/navigation';
import { isBefore, isPast, isSameDay, max, startOfDay } from 'app/lib/date';
import { isDonationListing } from 'app/lib/listing';
import { getLoanDatePickerConstraints } from 'app/lib/listing-loan-date-constraints';
import { getCurrentAndFutureUnavailabilityDays } from 'app/lib/unavailability';
import { isLender as isLenderFn } from 'app/lib/user';
import { useOneChannel } from 'app/network/chat/channel';
import type { Loan } from 'app/types/loan';
import { useCreateOneLoan, useOneLoan } from 'app/network/loan';
import { useUser } from 'app/network/user';
import type { Listing } from 'app/types/listing';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { WhiteLabel } from 'app/types/white-labels';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { usePageTitle } from 'app/utils/web-helpers';
import { eachDayOfInterval, isDate } from 'date-fns';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { type BatchUpdateListingLoanFeeDTO } from 'app/network/listing';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { useTranslation } from 'react-i18next';

import { LoanFeeFormFields, LoanFeeModals, schema as loanFeeSchema, useLoanFeeModals } from '../management/loan-fee';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconArrowLeft, IconCalendarWeek, IconInfoCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useMyProfile } from 'app/network/profile';
import {
  getAccessibleOfferDates,
  getPlannedSalePeriodReminderParams,
  shouldShowPerishableOfferDateRange,
} from './loan-dates-picker-date';
import { usePresentAppDownloadPrompt } from 'app/hooks/use-present-app-download-prompt';
const theme = createDSTheme();

function getTranslationSuffix({
  isDonation,
  isLender,
  isSell,
  isService,
}: {
  isDonation: boolean;
  isLender: boolean;
  isSell: boolean;
  isService: boolean;
}) {
  if (isService) return 'Service';
  if (isDonation) return 'Donation';
  if (isSell) return 'Sell';
  return isLender ? 'Lender' : 'Borrower';
}

function getPerishableOfferPeriod({
  isExistingLoan,
  listing,
  t,
}: {
  isExistingLoan: boolean;
  listing?: Listing;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const shouldShow = shouldShowPerishableOfferDateRange({
    isExistingLoan,
    isHauteGaspesie: isWhiteLabel(WhiteLabel.HauteGaspesie),
    listing,
  });
  if (!shouldShow || !listing?.start || !listing.end) return null;

  const dateFormat = t('dateFormat_MMMMdYYYY', { ns: 'common' });
  return getPlannedSalePeriodReminderParams(listing.start, listing.end, dateFormat);
}

function getPlannedSalePeriod({
  isSell,
  listing,
  loan,
  t,
}: {
  isSell: boolean;
  listing?: Listing;
  loan?: Loan | null;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  if (!loan || !isSell || !listing?.start || !listing.end) return null;

  const dateFormat = t('dateFormat_MMMMdYYYY', { ns: 'common' });
  return t('plannedSalePeriodReminder', getPlannedSalePeriodReminderParams(listing.start, listing.end, dateFormat));
}

function LoanDatesSubmitAccessory({
  isLender,
  isLoading,
  onPress,
  selectedEndDate,
  selectedStartDate,
  singleDate,
}: {
  isLender: boolean;
  isLoading: boolean;
  onPress: () => void;
  selectedEndDate: Date | null;
  selectedStartDate: Date | null;
  singleDate: boolean;
}) {
  const { t } = useTranslation('loanDatesPicker');
  const buttonText = isLender
    ? t(singleDate ? 'primaryButtonTextAsLenderSingleDate' : 'primaryButtonTextAsLender')
    : t('primaryButtonTextAsBorrower');

  return (
    <Accessory marginHorizontal="spacing.general.x4">
      <Button
        disabled={!selectedStartDate || !selectedEndDate}
        loading={isLoading}
        onPress={onPress}
        testID="loan-dates-picker-submit-button"
        variant="primary"
      >
        <Text color="surface.default" style={{ flex: 1 }} textAlign="center">
          {buttonText}
        </Text>
      </Button>
    </Accessory>
  );
}

const LoanDatesPickerScreenComponent: NavigationScreen<'loan-dates-picker'> = ({ navigation, route }) => {
  const channelQuery = useOneChannel(route.params.url);
  const { data: channel } = channelQuery;

  const listing = channel?.linkedOffer ?? channel?.listing;

  const loanQuery = useOneLoan(route.params.loanId);
  const loan = loanQuery.data;
  const isExistingLoan = Boolean(route.params.loanId);

  const { data: user } = useUser();
  const presentAppDownloadPrompt = usePresentAppDownloadPrompt();
  const { data: profile } = useMyProfile();
  const isLender = isLenderFn(user?.id, listing?.userId, listing?.type);

  const setPageTitle = usePageTitle();

  const isService = listing?.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(listing);
  const isSell = listing?.transactionMode === TransactionMode.sell;
  const singleDate = isService || isDonation || isSell;

  const loanFeeModalsState = useLoanFeeModals();

  const { t } = useTranslation('loanDatesPicker');
  const tSuffix = getTranslationSuffix({ isDonation, isLender, isSell, isService });

  const {
    selectedStartDate,
    selectedEndDate,
    unavailableDates,
    unavailableDateRanges,
    selectableUnavailableDates,
    selectableUnavailableDateRanges,
    userCurrentLoanDates,
    onDatePress,
    startOrEnd,
  } = useCalendar(listing, loan, singleDate, profile?.unavailability, user?.id);
  const { minimumDate, maximumDate } = getLoanDatePickerConstraints(listing, Boolean(loan));

  const perishableOfferPeriod = getPerishableOfferPeriod({ isExistingLoan, listing, t });

  const accessibleOfferDates = perishableOfferPeriod ? getAccessibleOfferDates(minimumDate, maximumDate) : [];

  useNavigationOptions(
    navigation,
    {
      headerBackVisible: false,
      headerLeft: () => (
        <Button onPress={navigation.goBack} accessibilityLabel={t('goBack', { ns: 'accessibility' })}>
          <IconArrowLeft strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['white-label.header.on-header']} />
        </Button>
      ),

      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t('title')}
        </Text>
      ),

      headerShown: true,
    },
    false,
  );

  useEffect(() => {
    setPageTitle(t('title'));
  }, [setPageTitle, t]);

  const { isPending: isLoading, mutate } = useCreateOneLoan({
    onSuccess: () => {
      navigation.goBack();
      presentAppDownloadPrompt({
        context: 'date-proposal',
        firstName: channel?.members.find((member) => member.user_id !== user?.id)?.firstname,
      });
    },
  });

  const loanFeeAvailable = isFeatureEnabled(FeatureFlag.loanFee, user?.featureFlags);

  const [loanFeeEnabled, setLoanFeeEnabled] = useState<boolean>(Boolean(loan?.fee));
  const flexibleDatesAvailable = isFeatureEnabled(FeatureFlag.flexibleDates, user?.featureFlags);

  const [flexibleDatesOverride, setFlexibleDatesOverride] = useState<boolean>();
  const flexibleDates = flexibleDatesOverride ?? Boolean(loan?.flexibleDates);

  const plannedSalePeriod = getPlannedSalePeriod({ isSell, listing, loan, t });

  const form = useForm<BatchUpdateListingLoanFeeDTO>({ schema: loanFeeSchema });
  const { getValues, setValue } = form;

  useEffect(() => {
    if (!loan?.fee) return;
    setValue('loanFee', loan.fee);
    setValue('loanFeeDescription', loan.feeDescription);
    setValue('loanFeePaymentMethod', loan.feePaymentMethod);
  }, [loan?.fee, loan?.feeDescription, loan?.feePaymentMethod, setValue]);

  const onSavePress = () => {
    if (selectedStartDate && selectedEndDate) {
      const loanFeesValues = getValues();
      mutate({
        channelURL: route.params.url,
        end: selectedEndDate,
        start: selectedStartDate,
        isUpdateFor: loan?.id,
        fee: loanFeesValues.loanFee ? parseFloat(loanFeesValues.loanFee as unknown as string) : null,
        feeDescription: loanFeesValues.loanFeeDescription ?? null,
        feePaymentMethod: loanFeesValues.loanFeePaymentMethod ?? null,
        flexibleDates,
      });
    }
  };

  useRegisterLoading(isLoading, 'LoanDatesPickerScreen');

  return (
    <Page
      accessory={
        <LoanDatesSubmitAccessory
          isLender={isLender}
          isLoading={isLoading}
          onPress={onSavePress}
          selectedEndDate={selectedEndDate}
          selectedStartDate={selectedStartDate}
          singleDate={singleDate}
        />
      }
    >
      <Column gap="spacing.general.x4" paddingBottom="spacing.general.x4">
        {route.params.promptIntroduceYourself && listing?.type === ListingType.offer ? (
          <Row backgroundColor="surface.inverse.tertiary-neutral" borderRadius="radius.lg" padding="spacing.general.x4">
            <Row flex={1}>
              <InfoChat color={theme.colors['surface.tertiary']} />
            </Row>

            <Text flex={7} fontSize={14} fontWeight="400">
              {t('promptIntroduceYourself')}
            </Text>
          </Row>
        ) : null}

        <Row backgroundColor="surface.inverse.tertiary-neutral" borderRadius="radius.lg" padding="spacing.general.x4">
          <Row flex={1}>
            <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
          </Row>

          <Text flex={7} fontSize={14} fontWeight="400">
            {loan ? t(`informationTextForExistingLoanAs${tSuffix}`) : t(`informationTextAs${tSuffix}`)}
          </Text>
        </Row>
        {plannedSalePeriod ? (
          <Text color="text.secondary" variant="bodyBase">
            {plannedSalePeriod}
          </Text>
        ) : null}
        {perishableOfferPeriod ? (
          <Row alignItems="center" gap="spacing.general.x2">
            <Row
              alignItems="center"
              backgroundColor="surface.secondary"
              borderRadius="radius.full"
              height={32}
              justifyContent="center"
              width={32}
            >
              <IconCalendarWeek color={theme.colors['text.primary']} size={16} strokeWidth={ICON_STROKE_WIDTH} />
            </Row>
            <Column flex={1} gap="spacing.general.x1">
              <Text variant="bodySmall">{t('offerDates')}</Text>
              <Text variant="bodyBaseMedium">
                {t('offerDateRange', {
                  start: perishableOfferPeriod.start,
                  end: perishableOfferPeriod.end,
                })}
              </Text>
            </Column>
          </Row>
        ) : null}
        <Calendar
          {...{ onDatePress }}
          availableDates={accessibleOfferDates}
          initial={selectedStartDate}
          minDate={minimumDate}
          maxDate={maximumDate}
          selected={[selectedStartDate, selectedEndDate].filter((d): d is Date => isDate(d))}
          unavailableDates={unavailableDates}
          unavailableDateRanges={unavailableDateRanges}
          selectableUnavailableDates={selectableUnavailableDates}
          selectableUnavailableDateRanges={selectableUnavailableDateRanges}
          userCurrentLoanDates={userCurrentLoanDates}
          willNowChoose={startOrEnd}
        />

        {flexibleDatesAvailable ? (
          <Row alignItems="center" gap="spacing.general.x2">
            <Checkbox checked={flexibleDates} onPress={() => setFlexibleDatesOverride(!flexibleDates)} />
            <Text variant="bodyBase">{t('flexibleDates')}</Text>
          </Row>
        ) : null}

        {loanFeeAvailable && listing?.type === ListingType.request && user?.id !== listing.userId ? (
          <FormProvider {...form}>
            <LoanFeeFormFields {...{ loanFeeEnabled, setLoanFeeEnabled, modalsState: loanFeeModalsState }} />
          </FormProvider>
        ) : null}
      </Column>
      <LoanFeeModals state={loanFeeModalsState} />
    </Page>
  );
};

export const LoanDatesPickerScreen: NavigationScreen<'loan-dates-picker'> = withOptionalMetrics(
  'LoanDatesPickerScreen',
  withErrorBoundary(LoanDatesPickerScreenComponent),
);

export function useCalendar(
  listing?: Listing,
  loan?: Loan | null,
  singleDate = false,
  userUnavailability?: { start: string; end: string }[] | null,
  currentUserId?: string,
) {
  const todayTimestamp = startOfDay(new Date()).getTime();
  const today = new Date(todayTimestamp);

  const [chosenStart, setChosenStart] = useState<Date | null>(() => {
    // For a loan, we use the start date
    if (loan) {
      return new Date(loan.start);
    }

    // For offers listings (and sometimes for requests too), we have no dates
    if (!listing?.start || !listing.end) {
      return null;
    }

    // In this case, we have three possible scenarios:
    // - The `start` date is in the future -> select it by default
    // - The `start` date is in the past, but `end` is in the future -> select today
    // - Both `start` and `end` are in the past -> select nothing
    const start = new Date(listing.start);
    const end = new Date(listing.end);
    return isPast(end) ? null : max([start, today]);
  });

  const [chosenEnd, setChosenEnd] = useState<Date | null>(() => {
    // For a loan, we use the end date
    if (loan) {
      return new Date(loan.end);
    }

    // For offers listings (and sometimes for requests too), we have no dates
    if (!listing?.end) {
      return null;
    }

    // Should the listing be expired (the specified date range is in the past), we select nothing by default. Otherwise, we select it.
    const end = new Date(listing.end);
    return isPast(end) ? null : end;
  });

  useEffect(() => {
    if (singleDate && chosenStart && (!chosenEnd || !isSameDay(chosenStart, chosenEnd))) {
      let cancelled = false;
      void Promise.resolve().then(() => {
        if (!cancelled) setChosenEnd(chosenStart);
      });
      return () => {
        cancelled = true;
      };
    }

    return undefined;
  }, [chosenEnd, chosenStart, singleDate]);

  const loanId = loan?.id;
  const userCurrentLoanDates =
    listing?.loans?.flatMap(({ id: userParticipateInThisLoanId, start, end }) =>
      loanId && loanId === userParticipateInThisLoanId
        ? eachDayOfInterval({ start: new Date(start), end: new Date(end) })
        : [],
    ) ?? [];

  // Linked offers can omit the embedded owner profile while the related request loads.
  const ownerUnavailability = (listing?.user as Partial<NonNullable<Listing['user']>> | undefined)?.profile
    ?.unavailability;

  const unavailableDateRanges = [
    ...(listing?.loans?.flatMap(({ id: userParticipateInThisLoanId, start, end }) =>
      !loanId || loanId !== userParticipateInThisLoanId
        ? [eachDayOfInterval({ start: new Date(start), end: new Date(end) })]
        : [],
    ) ?? []),
    // A proposer can choose dates during their own unavailability. The other party's
    // unavailability remains unavailable for proposals.
    ...(listing?.userId !== currentUserId
      ? (ownerUnavailability?.flatMap(({ start, end }) => {
          const currentAndFutureDays = getCurrentAndFutureUnavailabilityDays({ start, end }, today);

          return currentAndFutureDays.length > 0 ? [currentAndFutureDays] : [];
        }) ?? [])
      : []),
  ];

  const unavailableDates = unavailableDateRanges.flat();

  const selectableUnavailableDateRanges =
    userUnavailability?.flatMap(({ start, end }) => {
      const currentAndFutureDays = getCurrentAndFutureUnavailabilityDays({ start, end }, today);

      return currentAndFutureDays.length > 0 ? [currentAndFutureDays] : [];
    }) ?? [];

  const selectableUnavailableDates = selectableUnavailableDateRanges.flat();

  const clearDates = () => {
    setChosenStart(null);
    setChosenEnd(null);
  };

  const [willNowChoose, setWillNowChoose] = useState<'start' | 'end'>('start');
  const [previouslyChosenDate, setPreviouslyChosenDate] = useState<Date | null>(null);

  const unavailableDatesKey = unavailableDates.map((date) => startOfDay(date).toISOString()).join('|');

  useEffect(() => {
    let cancelled = false;
    const unavailableDatesSet = new Set(unavailableDatesKey ? unavailableDatesKey.split('|') : []);

    void Promise.resolve().then(() => {
      if (cancelled) return;

      if (chosenStart && chosenEnd) {
        const days = eachDayOfInterval({ start: chosenStart, end: chosenEnd });
        const hasUnavailable = days.some((day) => unavailableDatesSet.has(startOfDay(day).toISOString()));
        if (hasUnavailable) {
          setChosenStart(previouslyChosenDate);
          setChosenEnd(previouslyChosenDate);
        }
      }

      if (chosenStart && chosenEnd && isBefore(chosenEnd, chosenStart)) {
        setChosenStart(chosenEnd);
        setChosenEnd(chosenStart);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [chosenStart, chosenEnd, willNowChoose, unavailableDatesKey, previouslyChosenDate]);

  const onDatePress = (date: Date) => {
    setPreviouslyChosenDate(date);
    const isSameAsEnd = chosenEnd && isSameDay(date, chosenEnd);
    const isSameAsStart = chosenStart && isSameDay(date, chosenStart);

    if (singleDate) {
      setChosenStart(date);
      setChosenEnd(date);
      return;
    }

    if (isSameAsEnd && isSameAsStart) {
      /**
       * When pressing on a date that is both `start` and `end`,
       * we must clear both.
       */
      clearDates();
    } else if (isSameAsStart) {
      /**
       * When clicking on the `start` date,
       * we must reset it to `end` to allow for
       * picking a single date.
       */
      setChosenStart(chosenEnd);
    } else if (isSameAsEnd) {
      /**
       * When clicking on the `end` date,
       * we must reset it to `start` to allow for
       * picking a single date.
       */
      setChosenEnd(chosenStart);
    } else if (!chosenStart && !chosenEnd) {
      /**
       * When no date was selected before pressing,
       * we initialize both `start` and `end` to the
       * given value to allow for picking a single date.
       */
      setChosenStart(date);
      setChosenEnd(date);
    } else if (chosenStart && isBefore(date, chosenStart)) {
      /**
       * When pressing on date that's before `start`,
       * we move `start` back without touching `end`.
       */
      setChosenStart(date);
      setWillNowChoose('end');
    } else if (chosenEnd && isBefore(chosenEnd, date)) {
      setChosenEnd(date);
      setWillNowChoose('start');
    } else {
      if (willNowChoose === 'end') {
        setChosenEnd(date);
        setWillNowChoose('start');
      } else {
        setChosenStart(date);
        setWillNowChoose('end');
      }
    }
  };

  return {
    selectedStartDate: chosenStart,
    selectedEndDate: chosenEnd,
    unavailableDates,
    unavailableDateRanges,
    selectableUnavailableDates,
    selectableUnavailableDateRanges,
    userCurrentLoanDates,
    onDatePress,
    clearDates,
    startOrEnd: willNowChoose,
  };
}
